import React from 'react';
import { motion } from 'framer-motion';

const features = [
  {
    icon: '📋',
    title: 'Display Key Details',
    description: 'Menu, location, hours, gallery, and more - all at your guests\' fingertips.',
  },
  {
    icon: '💬',
    title: 'Handle Enquiries 24/7',
    description: 'Bookings, dishes, directions - your AI assistant never sleeps.',
  },
  {
    icon: '🎨',
    title: 'Fully Branded Experience',
    description: 'Custom logos, voice, and language that match your restaurant perfectly.',
  },
  {
    icon: '🌍',
    title: 'Multilingual Support',
    description: 'We speak your customer\'s language - over 100 languages supported.',
  },
  {
    icon: '⚙️',
    title: 'Highly Customizable',
    description: 'Adapts to your workflow, tone, and unique needs seamlessly.',
  },
];

const KairosCore: React.FC = () => {
  return (
    <section id="core" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-width-container">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Kairos Core
          </h2>
          <p className="text-xl text-slate-700 max-w-3xl mx-auto">
            "Your smart AI assistant for every restaurant front desk."
          </p>
        </motion.div>

        <motion.div 
          className="bg-white rounded-3xl shadow-xl p-8 lg:p-12 mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h3 className="text-2xl font-semibold text-slate-900 mb-8">
            We help restaurants:
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="relative group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-100 to-emerald-100 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative bg-white border border-gray-100 rounded-xl p-6 group-hover:border-transparent transition-all duration-300">
                  <motion.div 
                    className="text-4xl mb-4"
                    whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    {feature.icon}
                  </motion.div>
                  <h4 className="text-lg font-semibold text-slate-900 mb-2">
                    ✅ {feature.title}
                  </h4>
                  <p className="text-slate-600 text-sm">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div 
          className="text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-2xl p-8 inline-block">
            <p className="text-lg text-slate-800 font-medium">
              Let Kairos be your <span className="text-blue-600">invisible yet powerful</span> concierge.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default KairosCore;